const UTM_STORAGE_KEY = 'LENTHO_UTM_PARAMS';
const UTM_EXPIRY_DAYS = 30;

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  expires_at: number;
}

/**
 * Parse UTM parameters from URL search params
 */
export function parseUTMParams(
  searchParams: URLSearchParams
): UTMParams | null {
  const utm_source = searchParams.get('utm_source');
  const utm_medium = searchParams.get('utm_medium');
  const utm_campaign = searchParams.get('utm_campaign');
  const utm_term = searchParams.get('utm_term');
  const utm_content = searchParams.get('utm_content');

  // Only store if at least one UTM parameter exists
  if (
    !utm_source &&
    !utm_medium &&
    !utm_campaign &&
    !utm_term &&
    !utm_content
  ) {
    return null;
  }

  const expires_at = Date.now() + UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  return {
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_term && { utm_term }),
    ...(utm_content && { utm_content }),
    expires_at,
  };
}

/**
 * Save UTM parameters to localStorage with expiration
 */
export function saveUTMParams(utmParams: UTMParams): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  } catch (error) {
    console.error('Error saving UTM params:', error);
  }
}

/**
 * Get UTM parameters from localStorage if not expired
 */
export function getUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const storedData = localStorage.getItem(UTM_STORAGE_KEY);
    if (!storedData) return null;

    const utmParams: UTMParams = JSON.parse(storedData);

    // Check if expired
    if (Date.now() > utmParams.expires_at) {
      clearUTMParams();
      return null;
    }

    return utmParams;
  } catch (error) {
    console.error('Error getting UTM params:', error);
    return null;
  }
}

/**
 * Clear UTM parameters from localStorage
 */
export function clearUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing UTM params:', error);
  }
}

/**
 * Get UTM params as a plain object without expires_at
 */
export function getUTMParamsForTracking(): Omit<
  UTMParams,
  'expires_at'
> | null {
  const utmParams = getUTMParams();
  if (!utmParams) return null;

  const { expires_at, ...trackingParams } = utmParams;
  return trackingParams;
}
